import {
  type IconDefinition,
  faBan,
  faFileContract,
  faTrashCan,
  faRotateLeft,
  faArrowRotateLeft,
  faFlagSwallowtail,
  faLink,
  faArrowUpFromArc,
  faArrowDownToArc,
  faEnvelope,
} from '@fortawesome/pro-regular-svg-icons'
import { type MantineColor } from '@mantine/core'
import { useRouter } from 'next/router'
import { type ReactNode, useCallback, useMemo } from 'react'

import {
  useQueryGetAllBookingProductsUnavailable,
  useQueryGetAllBookingProducts,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useBookingStore } from '../../booking-details/hooks/useBookingStore'
import { useModalBookingProductsUnavailable } from '../../booking-products'
import { useModalBookingActionCancel } from './useModalBookingActionCancel'
import { useModalBookingActionChangeStatusToQuoted } from './useModalBookingActionChangeStatusToQuoted'
import { useModalBookingActionCreateQuote } from './useModalBookingActionCreateQuote'
import { useModalBookingActionDelete } from './useModalBookingActionDelete'
import { useModalBookingActionFlectoLink } from './useModalBookingActionFlectoLink'
import { useModalBookingActionReserve } from './useModalBookingActionReserve'
import { useModalBookingActionRestoreCancel } from './useModalBookingActionRestoreCancel'
import { useModalBookingActionRevertToDraft } from './useModalBookingActionRevertToDraft'
import { useModalBookingActionRevertToNegotiation } from './useModalBookingActionRevertToNegotiation'
import { useModalBookingActionRevertToOngoing } from './useModalBookingActionRevertToOngoing'
import { useModalBookingActionRevertToReserved } from './useModalBookingActionRevertToReserved'
import { useModalBookingSendEmail } from './useModalBookingSendEmail'

export type UseBookingActionsProps = {
  bookingId?: ParameterID
  excludeActions?: BookingActionType[]
  onSuccessActions?: Partial<Record<BookingActionType, () => void>>
}
export type BookingActionOption = { label: string; value: string }

export type BookingActionDefinition = {
  label: ReactNode
  color: MantineColor
  icon: IconDefinition
  useSubMenu?: boolean
  key: BookingActionType
  options?: BookingActionOption[]
  action: (option?: BookingActionOption) => void
}

export type BookingActionType =
  | 'revert_to_draft'
  | 'revert_to_ongoing'
  | 'revert_to_reserved'
  | 'revert_to_negotiation'
  | 'create_quote'
  | 'change_status_to_quoted'
  | 'reserve'
  | 'restore_cancel'
  | 'view_flecto_link'
  | 'pickup'
  | 'return'
  | 'cancel_booking'
  | 'delete_booking'
  | 'send_email'

export function useBookingActions({
  bookingId,
  excludeActions,
  onSuccessActions,
}: UseBookingActionsProps) {
  const router = useRouter()
  const { t } = useTranslation('saas', { keyPrefix: 'booking_actions' })
  const { showNotification } = useNotifications()

  // todo: remove this call to use bookingStore instead
  const { booking } = useBookingStore()

  const { data: bookingProducts } = useQueryGetAllBookingProducts({
    bookingId,
  })

  const hasCustomer = Boolean(booking?.customer)

  const itemToPickup = bookingProducts?.find(
    (item) => item.picked_up_quantity < item.quantity
  )

  const noHasCustomerNotification = () => {
    showNotification({
      type: 'error',
      message: t('notification_no_has_customer'),
    })
  }

  const bookingStatus = booking?.status

  const { openModal: openModalFlectoLink } = useModalBookingActionFlectoLink({
    bookingId,
  })

  const { openModal: openModalDelete } = useModalBookingActionDelete({
    bookingId,
    onSuccess: onSuccessActions?.delete_booking,
  })

  const { openModal: openModalSendEmail } = useModalBookingSendEmail({
    onSuccess: onSuccessActions?.send_email,
  })

  const { openModal: openModalCancel } = useModalBookingActionCancel({
    bookingId,
    onSuccess: onSuccessActions?.cancel_booking,
  })

  const { openModal: openModalRevertToDraft } =
    useModalBookingActionRevertToDraft({
      bookingId,
      onSuccess: onSuccessActions?.revert_to_draft,
    })

  const { openModal: openModalRevertToNegotiation } =
    useModalBookingActionRevertToNegotiation({
      bookingId,
      onSuccess: onSuccessActions?.revert_to_negotiation,
    })

  const { openModal: openModalRevertToOngoing } =
    useModalBookingActionRevertToOngoing({
      bookingId,
      onSuccess: onSuccessActions?.revert_to_ongoing,
    })

  const { openModal: openModalRevertToReserved } =
    useModalBookingActionRevertToReserved({
      bookingId,
      onSuccess: onSuccessActions?.revert_to_reserved,
    })

  const { openModal: openModalCreateQuote } = useModalBookingActionCreateQuote({
    bookingId,
    onSuccess: onSuccessActions?.create_quote,
  })

  const { openModal: openModalChangeToQuoted } =
    useModalBookingActionChangeStatusToQuoted({
      bookingId,
      onSuccess: onSuccessActions?.change_status_to_quoted,
    })

  const { openModal: openModalReserve } = useModalBookingActionReserve({
    bookingId,
    onSuccess: onSuccessActions?.reserve,
  })

  const { openModal: openModalRestoreCancel } =
    useModalBookingActionRestoreCancel({
      bookingId,
      onSuccess: onSuccessActions?.restore_cancel,
    })

  const { openModal: openModalBookingProductsUnavailable } =
    useModalBookingProductsUnavailable({
      bookingId,
    })

  const { data: unavailableItems } = useQueryGetAllBookingProductsUnavailable({
    bookingId,
  })

  const allActions: BookingActionDefinition[] = useMemo(() => {
    const actions = [
      {
        key: 'revert_to_draft' as BookingActionType,
        color: 'gray',
        icon: faArrowRotateLeft,
        label: t('action_revert_to_draft'),
        action: () => openModalRevertToDraft(),
      },
      {
        key: 'revert_to_ongoing' as BookingActionType,
        color: 'orange',
        icon: faArrowRotateLeft,
        label: t('action_revert_to_ongoing'),
        action: () => openModalRevertToOngoing(),
      },
      {
        key: 'revert_to_reserved' as BookingActionType,
        color: 'blue',
        icon: faArrowRotateLeft,
        label: t('action_revert_to_reserved'),
        action: () => openModalRevertToReserved(),
      },
      {
        key: 'revert_to_negotiation' as BookingActionType,
        color: 'purple',
        icon: faArrowRotateLeft,
        label: t('action_revert_to_negotiation'),
        action: () => openModalRevertToNegotiation(),
      },
      {
        key: 'create_quote' as BookingActionType,
        color: 'gray',
        icon: faFileContract,
        label: t('action_create_quote'),
        action: () => openModalCreateQuote(),
      },
      {
        key: 'change_status_to_quoted' as BookingActionType,
        color: 'gray',
        icon: faFileContract,
        label: t('action_change_status_to_quoted'),
        action: () => openModalChangeToQuoted(),
      },
      {
        key: 'reserve' as BookingActionType,
        color: 'blue',
        icon: faFlagSwallowtail,
        label: t('action_reserve'),
        action: () => {
          if (unavailableItems.length > 0) {
            openModalBookingProductsUnavailable()
          } else {
            openModalReserve()
          }
        },
      },
      {
        key: 'restore_cancel' as BookingActionType,
        color: 'gray',
        icon: faRotateLeft,
        label: t('action_restore_cancel'),
        action: () => openModalRestoreCancel(),
      },
      {
        key: 'return' as BookingActionType,
        icon: faArrowDownToArc,
        color: 'green',
        label:
          booking?.status === 'completed'
            ? t('action_edit_return')
            : t('action_return'),
        action: () => router.push(`/bookings/${bookingId}?view=return`),
      },
      {
        key: 'view_flecto_link' as BookingActionType,
        color: 'green',
        icon: faLink,
        label: t('action_view_flecto_link'),
        action: () => openModalFlectoLink(),
      },
      {
        key: 'send_email' as BookingActionType,
        icon: faEnvelope,
        color: 'green',
        useSubMenu: true,
        options: [
          { label: t('custom_template_label'), value: 'custom' },
          { label: t('quote_template_label'), value: 'quote' },
          { label: t('contract_template_label'), value: 'contract' },
          { label: t('invoice_template_label'), value: 'invoice' },
          { label: t('proforma_template_label'), value: 'proforma' },
        ],
        label: t('action_send_email'),

        action: (template?: BookingActionOption) => {
          if (template?.value) {
            openModalSendEmail({ template })
          } else {
            console.error('No template selected')
          }
        },
      },
      {
        key: 'cancel_booking' as BookingActionType,
        color: 'red',
        icon: faBan,
        label: t('action_cancel_booking'),
        action: () => openModalCancel(),
      },
      {
        key: 'delete_booking' as BookingActionType,
        color: 'red',
        icon: faTrashCan,
        label: t('action_delete_booking'),
        action: () => openModalDelete(),
      },
    ]

    if (itemToPickup) {
      const pickupAction = {
        key: 'pickup' as BookingActionType,
        icon: faArrowUpFromArc,
        color: 'orange',
        label: t('action_pickup'),
        action: () => {
          if (unavailableItems.length > 0) {
            openModalBookingProductsUnavailable()
          } else {
            router.push(`/bookings/${bookingId}?view=pickup`)
          }
        },
      }

      actions.splice(3, 0, pickupAction)
    }

    return actions
  }, [booking, unavailableItems, itemToPickup])

  const getActions = useCallback(
    (keys: BookingActionType[]) => {
      return allActions.filter((action) => keys.includes(action.key))
    },
    [allActions]
  )

  const getAction = useCallback(
    (key: BookingActionType) => {
      return allActions.find((action) => action.key === key)
    },
    [allActions]
  )

  const bookingActions = useMemo(() => {
    switch (booking?.status) {
      case 'drafting':
        return getActions([
          'create_quote',
          'reserve',
          'pickup',
          'view_flecto_link',
          'send_email',
          'delete_booking',
        ])

      case 'ongoing':
        return getActions([
          'pickup',
          'return',
          'revert_to_reserved',
          'view_flecto_link',
          'send_email',
          'cancel_booking',
        ])

      case 'negotiation':
      case 'negotiating':
        return getActions([
          'reserve',
          'pickup',
          'revert_to_draft',
          'view_flecto_link',
          'send_email',
          'cancel_booking',
        ])

      case 'completed':
        return getActions([
          'return',
          'revert_to_ongoing',
          'view_flecto_link',
          'send_email',
        ])

      case 'reserved':
        return getActions([
          'pickup',
          'revert_to_draft',
          'revert_to_negotiation',
          'view_flecto_link',
          'send_email',
          'cancel_booking',
        ])

      case 'canceled':
        return getActions(['restore_cancel', 'view_flecto_link', 'send_email'])

      default:
        return getActions(['view_flecto_link', 'send_email'])
    }
  }, [booking])

  return {
    getAction,
    getActions,
    allActions,
    hasCustomer,
    noHasCustomerNotification,
    bookingStatus,
    bookingActions: bookingActions.filter((action) =>
      excludeActions ? !excludeActions.includes(action.key) : bookingActions
    ),
  }
}
